/**
 * Tracking Context Utility
 * Provides common tracking context (session_id, anonymous_id, etc.) for analytics
 *
 * THE ONE BROWSER IDENTITY. Every table that records what a person did reads its
 * `session_id` and `anonymous_id` from here, and nowhere else.
 *
 * It was not always so, and the cost was total. Three modules each minted their
 * own pair into their own storage keys -- `utils/analytics.ts` (`sw_session_id` /
 * `sw_anonymous_id`) wrote `events`, this file (`session_id` / `anon_id`, uuid)
 * wrote `funnel_events`, and `utils/error-tracking.ts` (`sw_error_session_id` /
 * `sw_error_anonymous_id`) wrote `error_logs`. Same concept, same column name,
 * three unrelated values, so the tables LOOKED joinable and never joined.
 * Measured on production over 2026-09-08..15: 3,181 funnel sessions had 0 matches
 * in `events`; 886 error sessions and 759 error anonymous ids had 0 in either.
 *
 * That was the ceiling on every conversion question worth asking. "Which errors
 * did the people who abandoned checkout actually hit?" is one join, and the join
 * returned nothing -- indistinguishable from a clean checkout, forever.
 *
 * A previous repair (b05e8f394, 2026-08-26) was believed to have fixed this. It
 * pointed the funnel tracker at `getSessionId()` but left `analytics.ts` minting
 * its own, so funnels moved from one private namespace onto a SECOND one rather
 * than onto the shared one. The lesson is in the shape of this file now: the
 * generators are gone from the other modules entirely, not merely bypassed.
 *
 * WHY THESE KEYS WON. `sw_session_id` / `sw_anonymous_id` are the analytics keys,
 * kept because `events` is much the largest corpus and the one the ranking and
 * impression work reads -- keeping them means that history stays continuous and
 * returning visitors keep their identity. Values already stored under the retired
 * keys are ADOPTED rather than discarded, so a browser that only ever held a
 * funnel or error anonymous id carries it forward instead of arriving as new.
 */

import { v4 as uuidv4 } from 'uuid';
import { getRankingVariant } from './ranking-variant-store';

/** sessionStorage: the current visit. */
const SESSION_KEY = 'sw_session_id';
/** sessionStorage: when the visit was last active, for the idle timeout. */
const SESSION_TS_KEY = 'sw_session_ts';
/** localStorage: this browser, across visits. */
const ANON_KEY = 'sw_anonymous_id';

/**
 * Thirty minutes of inactivity ends a session.
 *
 * Taken from the analytics generator rather than this file's old behaviour of
 * never expiring, because a tab left open for three days was one "session" in
 * `funnel_events` and three in `events`, which is its own kind of disagreement.
 * The timestamp slides on every read, so active use never expires mid-flow.
 */
const SESSION_IDLE_MS = 30 * 60 * 1000;

/**
 * Anonymous ids written by the generators this module replaced.
 *
 * Read only when `sw_anonymous_id` is absent, in this order, so a browser that
 * has been here before keeps the identity it already had. Ordering is by corpus
 * size: `anon_id` (funnels) before `sw_error_anonymous_id` (errors).
 */
const LEGACY_ANON_KEYS = ['anon_id', 'sw_error_anonymous_id'];

/**
 * In-memory fallback, for a browser whose storage is blocked.
 *
 * The generator this replaces returned a FRESH id from its catch block on every
 * call, so a visitor in Safari private mode -- or anyone with site data blocked
 * -- produced one session per event and appeared in the data as an endless
 * stream of one-event strangers. Holding the id for the life of the page load
 * makes such a visit a single session, which is the truth about it.
 */
let fallbackSessionId = '';
let fallbackAnonId = '';
let fallbackCheckoutSessionId = '';

/** `<epoch-ms>-<9 random base36>`, the format `events` has always used. */
function newId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

/**
 * Last value written to each cookie, so a read does not rewrite an unchanged one.
 *
 * These getters are called on every tracked event; `document.cookie` is a
 * synchronous same-thread write and there is no reason to pay for it 40 times a
 * page when the value has not moved.
 */
const mirrored: Record<string, string> = {};

/**
 * Mirror an id into a first-party cookie so the SERVER can see it.
 *
 * This is what makes a server-side failure attributable to the visit that caused
 * it. 74% of `error_logs` rows (3,079 of 4,151 in a week) carried no session at
 * all, because a server error has no storage to read -- and the two readers that
 * tried (`utils/clickhouse/error-logging.ts`, `getSessionIdFromRequest` in
 * `utils/infrastructure-errors.ts`) looked for COOKIES named `sw_session_id` and
 * `session_id` that nothing has ever set. They were reading the right names; the
 * write side simply did not exist. It does now, so those readers start working
 * without a change at any call site.
 *
 * Deliberately not HttpOnly: the client owns these values and must be able to
 * read and rotate them. Lax, so they ride ordinary navigation and same-site
 * fetches but are not offered to third parties.
 */
function mirrorToCookie(name: string, value: string): void {
  // Only getSessionId calls this, after its own window check, so a document is here.
  if (mirrored[name] === value) return;
  try {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; SameSite=Lax${secure}`;
    mirrored[name] = value;
  // eslint-disable-next-line sellwild-code/catch-must-log -- the error tracker reads this module on every error; logging from here would recurse through the failure
  } catch {
    // A blocked cookie jar throws on assignment in some engines. The client-side
    // id still works; only server attribution is lost for this visitor.
  }
}

/**
 * Get or create a persistent anonymous ID for this browser.
 *
 * A FALLBACK now, not the source of truth. `proxy.ts` mints the real one into an
 * HttpOnly cookie the server sets, because Safari caps a cookie written from
 * JavaScript to seven days and deletes localStorage after seven days idle -- so
 * this value is precisely the one that evaporates. Every ingest endpoint prefers
 * the cookie and falls back to what the client sends, which keeps a caller with
 * no cookie (a native webview, a request that raced the proxy) attributable.
 *
 * Kept rather than deleted because that fallback is real, and because the
 * identity bridge needs a client-visible id to compare the retired keys against.
 */
export function getAnonymousId(): string {
  if (typeof window === 'undefined') return '';

  try {
    const stored = localStorage.getItem(ANON_KEY);
    if (stored) return stored;

    // Carry a retired key's value forward rather than minting a new identity for
    // a browser that has been here before.
    const adopted = LEGACY_ANON_KEYS.reduce<string | null>(
      (found, key) => found || localStorage.getItem(key),
      null
    );

    const anonId = adopted || newId();
    localStorage.setItem(ANON_KEY, anonId);
    return anonId;
  // eslint-disable-next-line sellwild-code/catch-must-log -- the error tracker reads this module on every error; logging from here would recurse through the failure
  } catch {
    if (!fallbackAnonId) fallbackAnonId = newId();
    return fallbackAnonId;
  }
}

/**
 * Get or create a session ID for this browser session
 */
export function getSessionId(): string {
  if (typeof window === 'undefined') return '';

  try {
    const stored = sessionStorage.getItem(SESSION_KEY);
    const lastSeen = parseInt(sessionStorage.getItem(SESSION_TS_KEY) || '', 10);

    // NaN from a missing or corrupt timestamp fails this comparison, which
    // starts a new session -- the safe direction.
    if (stored && Date.now() - lastSeen < SESSION_IDLE_MS) {
      sessionStorage.setItem(SESSION_TS_KEY, Date.now().toString());
      mirrorToCookie(SESSION_KEY, stored);
      return stored;
    }

    const sessionId = newId();
    sessionStorage.setItem(SESSION_KEY, sessionId);
    sessionStorage.setItem(SESSION_TS_KEY, Date.now().toString());
    mirrorToCookie(SESSION_KEY, sessionId);
    return sessionId;
  // eslint-disable-next-line sellwild-code/catch-must-log -- the error tracker reads this module on every error; logging from here would recurse through the failure
  } catch {
    if (!fallbackSessionId) fallbackSessionId = newId();
    return fallbackSessionId;
  }
}

/**
 * End the current session and start a fresh one. Called on logout, where
 * continuing to file the next person's events under the previous one's session
 * is the thing to avoid.
 */
export function startNewSession(): string {
  if (typeof window === 'undefined') return '';
  try {
    sessionStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(SESSION_TS_KEY);
  // eslint-disable-next-line sellwild-code/catch-must-log -- the error tracker reads this module on every error; logging from here would recurse through the failure
  } catch {
    fallbackSessionId = '';
  }
  delete mirrored[SESSION_KEY];
  return getSessionId();
}

/**
 * Get or create a checkout session ID for the current checkout flow
 */
export function getCheckoutSessionId(): string {
  if (typeof window === 'undefined') return '';

  // Guarded like the two above: this one runs inside checkout, where an
  // unhandled storage throw takes the payment form down with it.
  try {
    const STORAGE_KEY = 'checkout_session_id';
    let checkoutSessionId = sessionStorage.getItem(STORAGE_KEY);

    if (!checkoutSessionId) {
      checkoutSessionId = `checkout_${uuidv4()}`;
      sessionStorage.setItem(STORAGE_KEY, checkoutSessionId);
    }

    return checkoutSessionId;
  // eslint-disable-next-line sellwild-code/catch-must-log -- the error tracker reads this module on every error; logging from here would recurse through the failure
  } catch {
    if (!fallbackCheckoutSessionId) fallbackCheckoutSessionId = `checkout_${uuidv4()}`;
    return fallbackCheckoutSessionId;
  }
}

/**
 * Clear the checkout session ID (call after successful checkout)
 */
export function clearCheckoutSessionId(): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem('checkout_session_id');
  // eslint-disable-next-line sellwild-code/catch-must-log -- the error tracker reads this module on every error; logging from here would recurse through the failure
  } catch {
    fallbackCheckoutSessionId = '';
  }
}

/**
 * Get user agent string
 */
export function getUserAgent(): string {
  if (typeof window === 'undefined') return '';
  return navigator.userAgent || '';
}

/**
 * Get current page URL
 */
export function getPageUrl(): string {
  if (typeof window === 'undefined') return '';
  return window.location.href;
}

/**
 * Get referrer URL
 */
export function getReferrer(): string {
  if (typeof window === 'undefined') return '';
  return document.referrer || '';
}

/**
 * Get environment string
 */
export function getEnvironment(): string {
  return process.env.NEXT_PUBLIC_ENVIRONMENT ||
         process.env.NODE_ENV ||
         'development';
}

/**
 * Generate a fingerprint for an error (used for deduplication)
 */
export function generateErrorFingerprint(
  errorType: string,
  message: string,
  file?: string,
  line?: number
): string {
  const parts = [
    errorType,
    message.substring(0, 100), // First 100 chars of message
    file || '',
    line?.toString() || '',
  ].filter(Boolean);

  return parts.join('|');
}

/**
 * Get common tracking context for analytics events
 */
export interface TrackingContext {
  session_id: string;
  anonymous_id: string;
  user_agent: string;
  page_url: string;
  referrer: string;
  environment: string;
  ip_address: string;
  /**
   * Which ranking arm served this visit: a variant id, or `control`. Present on
   * every event so the arms can be compared without a join to anything.
   */
  ranking_variant: string;
}

export async function getTrackingContext(): Promise<TrackingContext> {
  // ip_address is always left empty: every consumer derives the real client IP
  // server-side from the request (x-forwarded-for / x-real-ip / socket), and
  // overwrites whatever the client sends. See pages/api/errors/log.ts
  // (getClientIp) and pages/api/analytics/funnel/track.ts.
  //
  // This used to await a third-party lookup (api.ipify.org) from the browser on
  // every tracked step, including in front of stripe.confirmPayment — a blocking
  // external round trip with no timeout, for a value that was then discarded.
  return {
    session_id: getSessionId(),
    anonymous_id: getAnonymousId(),
    user_agent: getUserAgent(),
    page_url: getPageUrl(),
    referrer: getReferrer(),
    environment: getEnvironment(),
    ip_address: '',
    ranking_variant: getRankingVariant(),
  };
}
